import React, { useState, useRef, useEffect } from 'react';
import { useSoaBuilder } from './hooks/useSoaBuilder';
import StepSidebar from './components/StepSidebar';
import StepClientDetails from './components/StepClientDetails';
import StepScope from './components/StepScope';
import StepCurrentSituation from './components/StepCurrentSituation';
import StepObjectives from './components/StepObjectives';
import StepRiskProfile from './components/StepRiskProfile';
import StepStrategy from './components/StepStrategy';
import StepRetirementProjection from './components/StepRetirementProjection';
import StepProductComparison from './components/StepProductComparison';
import StepFees from './components/StepFees';
import StepActionsAuthority from './components/StepActionsAuthority';
import StepWarnings from './components/StepWarnings';
import SoaPreview from './components/SoaPreview';
import ComplianceCheckModal from './components/ComplianceCheckModal';
import './SoaBuilderApp.css';

const SoaBuilderApp = () => {
  const hook = useSoaBuilder();
  const [showPreview, setShowPreview] = useState(false);
  const [confirmFinalise, setConfirmFinalise] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [renamingFolderId, setRenamingFolderId] = useState(null);
  const [renamingFolderName, setRenamingFolderName] = useState('');
  const [contextMenu, setContextMenu] = useState(null);
  const [moveMenuDraftId, setMoveMenuDraftId] = useState(null);
  const [shareModal, setShareModal] = useState(null); // { draftId, shares: [], domainUsers: [] }
  const [shareInput, setShareInput] = useState('');
  const [showAuditLog, setShowAuditLog] = useState(false);
  const searchInputRef = useRef(null);

  const {
    currentStep, setStep, nextStep, prevStep,
    stepDefs, stepOrder,
    formData, updateFormData,
    draftId, draftStatus, draftTitle, setDraftTitle,
    drafts, totalDrafts, createDraft, loadDraft, deleteDraft, finaliseDraft, resetForm,
    showDraftsList, setShowDraftsList,
    portfolios, selectedPortfolio, selectPortfolio, getFilteredPortfolios, riskProfiles,
    calculateBlend, searchFunds, fetchAssetClasses,
    exportPdf, isExporting,
    isSaving, isLoading, error, setError, lastSaved, isFinalised,
    // New features
    folders, selectedFolderId, setSelectedFolderId,
    createFolder, renameFolder, deleteFolder,
    searchQuery, setSearch, statusFilter, setStatusFilter,
    ownerFilter, setOwnerFilter, sortBy, setSortBy,
    currentPage, setCurrentPage, pageSize,
    duplicateDraft, moveDraft,
    togglePrivate, fetchDomainUsers, fetchDraftShares, shareDraft, unshareDraft,
    editConflict, reloadLatest, forceSave,
    auditLog, getAuditLog,
    complianceWarnings, setComplianceWarnings, runComplianceCheck,
    // Adviser profile, client, templates
    adviserProfile, useAdviserProfile, saveAdviserProfile,
    clientId, loadClientIntoForm, saveAsClient,
  } = hook;

  // Close context menu on outside click
  useEffect(() => {
    const handler = () => { setContextMenu(null); setMoveMenuDraftId(null); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // Browser back button: return to list view instead of leaving the app
  useEffect(() => {
    if (!showDraftsList) {
      // Entered editor — push a history entry so back returns to list
      window.history.pushState({ soaView: 'editor' }, '');
    }
    const handlePopState = (e) => {
      if (!showDraftsList) {
        setShowDraftsList(true);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showDraftsList, setShowDraftsList]);

  // Position context menu within viewport bounds
  const getMenuPosition = (clientX, clientY) => {
    const menuWidth = 220;
    const menuHeight = 200;
    const x = clientX + menuWidth > window.innerWidth ? clientX - menuWidth : clientX;
    const y = clientY + menuHeight > window.innerHeight ? clientY - menuHeight : clientY;
    return { x: Math.max(0, x), y: Math.max(0, y) };
  };

  const handleStartNew = async () => {
    const title = formData.clientDetails?.fullName
      ? `${formData.clientDetails.fullName} SOA`
      : 'New SOA';
    await createDraft(title);
  };

  const handleFinalise = async () => {
    if (!confirmFinalise) {
      setConfirmFinalise(true);
      return;
    }
    const result = await finaliseDraft();
    if (result?.complianceBlock) {
      // Compliance warnings detected — modal will show via complianceWarnings state
      setConfirmFinalise(false);
      return;
    }
    setConfirmFinalise(false);
  };

  const handleComplianceFinalise = async () => {
    await finaliseDraft({ acknowledgeWarnings: true });
    setComplianceWarnings(null);
  };

  const handleOpenAuditLog = async () => {
    await getAuditLog();
    setShowAuditLog(true);
  };

  // Map section key to step number for compliance "go to" links
  const goToSection = (sectionKey) => {
    const idx = stepOrder.indexOf(sectionKey);
    if (idx >= 0) setStep(idx + 1);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await createFolder(newFolderName.trim());
      setNewFolderName('');
      setCreatingFolder(false);
    } catch { /* error handled in hook */ }
  };

  const handleRenameFolder = async (folderId) => {
    if (!renamingFolderName.trim()) return;
    await renameFolder(folderId, renamingFolderName.trim());
    setRenamingFolderId(null);
    setRenamingFolderName('');
  };

  const openShareModal = async (draftId) => {
    const [shares, domainUsers] = await Promise.all([
      fetchDraftShares(draftId),
      fetchDomainUsers(),
    ]);
    setShareModal({ draftId, shares, domainUsers });
    setShareInput('');
  };

  const handleAddShare = async () => {
    if (!shareInput.trim() || !shareModal) return;
    await shareDraft(shareModal.draftId, [shareInput.trim()]);
    const shares = await fetchDraftShares(shareModal.draftId);
    setShareModal(prev => ({ ...prev, shares }));
    setShareInput('');
  };

  const handleRemoveShare = async (email) => {
    if (!shareModal) return;
    await unshareDraft(shareModal.draftId, email);
    const shares = await fetchDraftShares(shareModal.draftId);
    setShareModal(prev => ({ ...prev, shares }));
  };

  const totalPages = Math.ceil(totalDrafts / pageSize);

  const getAuthorName = (email) => {
    if (!email) return '—';
    return email.split('@')[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  };

  const renderStep = () => {
    const stepKey = stepOrder[currentStep - 1];
    const disabled = isFinalised;

    switch (stepKey) {
      case 'clientDetails':
        return <StepClientDetails formData={formData} updateFormData={updateFormData} nextStep={nextStep} disabled={disabled} onSelectClient={loadClientIntoForm} onSaveAsClient={saveAsClient} clientId={clientId} />;
      case 'scope':
        return <StepScope formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} disabled={disabled} />;
      case 'currentSituation':
        return <StepCurrentSituation formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} disabled={disabled} />;
      case 'objectives':
        return <StepObjectives formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} disabled={disabled} />;
      case 'riskProfile':
        return <StepRiskProfile formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} riskProfiles={riskProfiles} disabled={disabled} />;
      case 'strategy':
        return (
          <StepStrategy
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
            portfolios={portfolios}
            selectedPortfolio={selectedPortfolio}
            selectPortfolio={selectPortfolio}
            getFilteredPortfolios={getFilteredPortfolios}
            calculateBlend={calculateBlend}
            searchFunds={searchFunds}
            fetchAssetClasses={fetchAssetClasses}
            disabled={disabled}
          />
        );
      case 'retirementProjection':
        return (
          <StepRetirementProjection
            formData={formData}
            updateFormData={updateFormData}
            nextStep={nextStep}
            prevStep={prevStep}
            portfolios={portfolios}
            draftId={draftId}
            disabled={disabled}
          />
        );
      case 'productComparison':
        return <StepProductComparison formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} disabled={disabled} />;
      case 'fees':
        return <StepFees formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} selectedPortfolio={selectedPortfolio} disabled={disabled} />;
      case 'actionsAuthority':
        return <StepActionsAuthority formData={formData} updateFormData={updateFormData} nextStep={nextStep} prevStep={prevStep} disabled={disabled} />;
      case 'warnings':
        return <StepWarnings formData={formData} updateFormData={updateFormData} prevStep={prevStep} disabled={disabled} adviserProfile={adviserProfile} onUseAdviserProfile={useAdviserProfile} onSaveAdviserProfile={saveAdviserProfile} />;
      default:
        return null;
    }
  };

  // ── Drafts list view ──────────────────────────────────────────────
  if (showDraftsList) {
    // Count for "All" and "Unfiled"
    const allCount = totalDrafts;
    const unfiledCount = folders.length > 0
      ? allCount - folders.reduce((sum, f) => sum + (f.draft_count || 0), 0)
      : allCount;

    return (
      <div className="soa-app">
        <div className="container-fluid py-4 px-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="soa-page-title">Statements of Advice</h2>
            <button className="btn btn-primary" onClick={() => { resetForm(); setShowDraftsList(false); }}>
              <i className="bi bi-plus-lg me-2"></i>New SOA
            </button>
          </div>

          <div className="row g-3">
            {/* Folder sidebar */}
            <div className="col-lg-3">
              <div className="soa-folder-sidebar">
                <div className="soa-folder-header">Folders</div>

                <div
                  className={`soa-folder-item ${selectedFolderId === null ? 'soa-folder-active' : ''}`}
                  onClick={() => setSelectedFolderId(null)}
                >
                  <i className="bi bi-collection me-2"></i>
                  All SOAs
                  <span className="soa-folder-count">{allCount}</span>
                </div>

                <div
                  className={`soa-folder-item ${selectedFolderId === 'unfiled' ? 'soa-folder-active' : ''}`}
                  onClick={() => setSelectedFolderId('unfiled')}
                >
                  <i className="bi bi-file-earmark me-2"></i>
                  Unfiled
                  <span className="soa-folder-count">{unfiledCount >= 0 ? unfiledCount : '—'}</span>
                </div>

                {folders.length > 0 && <hr className="my-2" />}

                {folders.map(folder => (
                  <div key={folder.id}>
                    {renamingFolderId === folder.id ? (
                      <div className="soa-folder-item d-flex gap-1 align-items-center">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={renamingFolderName}
                          onChange={(e) => setRenamingFolderName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleRenameFolder(folder.id); if (e.key === 'Escape') setRenamingFolderId(null); }}
                          autoFocus
                        />
                        <button className="btn btn-sm btn-primary" onClick={() => handleRenameFolder(folder.id)}>
                          <i className="bi bi-check"></i>
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`soa-folder-item ${selectedFolderId === folder.id ? 'soa-folder-active' : ''}`}
                        onClick={() => setSelectedFolderId(folder.id)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          setContextMenu({ type: 'folder', id: folder.id, ...getMenuPosition(e.clientX, e.clientY) });
                        }}
                      >
                        <i className="bi bi-folder me-2"></i>
                        {folder.name}
                        <span className="soa-folder-count">{folder.draft_count || 0}</span>
                      </div>
                    )}
                  </div>
                ))}

                {creatingFolder ? (
                  <div className="soa-folder-item d-flex gap-1 align-items-center">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Folder name..."
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') setCreatingFolder(false); }}
                      autoFocus
                    />
                    <button className="btn btn-sm btn-primary" onClick={handleCreateFolder}>
                      <i className="bi bi-check"></i>
                    </button>
                  </div>
                ) : (
                  <div className="soa-folder-item soa-folder-add" onClick={() => setCreatingFolder(true)}>
                    <i className="bi bi-plus me-2"></i>New Folder
                  </div>
                )}
              </div>
            </div>

            {/* Main content */}
            <div className="col-lg-9">
              {/* Toolbar */}
              <div className="soa-search-bar">
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <div className="flex-grow-1" style={{ maxWidth: '320px' }}>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text"><i className="bi bi-search"></i></span>
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="form-control"
                        placeholder="Search SOAs..."
                        defaultValue={searchQuery}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="soa-filter-chips">
                    <button
                      className={`btn btn-sm ${!statusFilter ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setStatusFilter(null)}
                    >All</button>
                    <button
                      className={`btn btn-sm ${statusFilter === 'draft' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setStatusFilter('draft')}
                    >Draft</button>
                    <button
                      className={`btn btn-sm ${statusFilter === 'finalised' ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => setStatusFilter('finalised')}
                    >Finalised</button>
                  </div>

                  <button
                    className={`btn btn-sm ${ownerFilter === 'mine' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setOwnerFilter(ownerFilter === 'mine' ? null : 'mine')}
                  >
                    <i className="bi bi-person me-1"></i>My SOAs
                  </button>
                </div>
              </div>

              {/* Table */}
              {drafts.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-file-earmark-text" style={{ fontSize: '3rem', color: 'var(--gray-color)' }}></i>
                  <p className="mt-3 text-muted">
                    {searchQuery || statusFilter || ownerFilter || selectedFolderId
                      ? 'No SOAs match your filters.'
                      : 'No SOA drafts yet. Create your first one.'}
                  </p>
                  {!searchQuery && !statusFilter && !ownerFilter && !selectedFolderId && (
                    <button className="btn btn-primary mt-2" onClick={() => setShowDraftsList(false)}>
                      <i className="bi bi-plus-lg me-2"></i>Create SOA
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="soa-drafts-table">
                    <table className="table table-hover mb-0">
                      <thead>
                        <tr>
                          <th
                            className="sortable-col"
                            onClick={() => setSortBy(sortBy === 'title' ? 'updated' : 'title')}
                          >
                            Title {sortBy === 'title' && <i className="bi bi-chevron-up"></i>}
                          </th>
                          <th
                            className="sortable-col"
                            onClick={() => setSortBy(sortBy === 'client' ? 'updated' : 'client')}
                          >
                            Client {sortBy === 'client' && <i className="bi bi-chevron-up"></i>}
                          </th>
                          <th>Status</th>
                          <th>Author</th>
                          <th
                            className="sortable-col"
                            onClick={() => setSortBy(sortBy === 'updated' ? 'created' : 'updated')}
                          >
                            Updated {sortBy === 'updated' && <i className="bi bi-chevron-down"></i>}
                          </th>
                          <th style={{ width: '48px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {drafts.map(draft => (
                          <tr key={draft.id} className="soa-draft-row" onClick={() => loadDraft(draft.id)}>
                            <td>
                              <div className="d-flex align-items-center gap-1">
                                {draft.is_private && <i className="bi bi-lock-fill text-muted" title="Private"></i>}
                                <span className="fw-semibold">{draft.title || 'Untitled SOA'}</span>
                              </div>
                            </td>
                            <td className="text-muted">{draft.client_name || '—'}</td>
                            <td>
                              <span className={`badge ${draft.status === 'finalised' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                {draft.status === 'finalised' ? 'Finalised' : 'Draft'}
                              </span>
                            </td>
                            <td className="text-muted">{getAuthorName(draft.created_by_email)}</td>
                            <td className="text-muted small">{formatDate(draft.updated_at)}</td>
                            <td>
                              <div className="dropdown">
                                <button
                                  className="btn btn-sm btn-light"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setContextMenu(
                                      contextMenu?.id === draft.id ? null : { type: 'draft', id: draft.id, ...getMenuPosition(e.clientX, e.clientY) }
                                    );
                                  }}
                                >
                                  <i className="bi bi-three-dots-vertical"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="soa-pagination">
                      <span className="text-muted small">
                        Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalDrafts)} of {totalDrafts}
                      </span>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          disabled={currentPage <= 1}
                          onClick={() => setCurrentPage(currentPage - 1)}
                        >&laquo;</button>
                        {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                          let page;
                          if (totalPages <= 7) {
                            page = i + 1;
                          } else if (currentPage <= 4) {
                            page = i + 1;
                          } else if (currentPage >= totalPages - 3) {
                            page = totalPages - 6 + i;
                          } else {
                            page = currentPage - 3 + i;
                          }
                          return (
                            <button
                              key={page}
                              className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-outline-secondary'}`}
                              onClick={() => setCurrentPage(page)}
                            >{page}</button>
                          );
                        })}
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          disabled={currentPage >= totalPages}
                          onClick={() => setCurrentPage(currentPage + 1)}
                        >&raquo;</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Context menu (draft actions) */}
          {contextMenu?.type === 'draft' && (
            <div
              className="soa-context-menu"
              style={{ top: contextMenu.y, left: contextMenu.x }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => { loadDraft(contextMenu.id); setContextMenu(null); }}>
                <i className="bi bi-pencil me-2"></i>Edit
              </button>
              <button onClick={() => { duplicateDraft(contextMenu.id); setContextMenu(null); }}>
                <i className="bi bi-copy me-2"></i>Duplicate
              </button>
              <button onClick={() => { setMoveMenuDraftId(contextMenu.id); }}>
                <i className="bi bi-folder-symlink me-2"></i>Move to Folder
              </button>
              {moveMenuDraftId === contextMenu.id && (
                <div className="soa-move-submenu">
                  <button onClick={() => { moveDraft(contextMenu.id, null); setContextMenu(null); setMoveMenuDraftId(null); }}>
                    <i className="bi bi-file-earmark me-2"></i>Unfiled
                  </button>
                  {folders.map(f => (
                    <button key={f.id} onClick={() => { moveDraft(contextMenu.id, f.id); setContextMenu(null); setMoveMenuDraftId(null); }}>
                      <i className="bi bi-folder me-2"></i>{f.name}
                    </button>
                  ))}
                </div>
              )}
              <button onClick={() => {
                const draft = drafts.find(d => d.id === contextMenu.id);
                togglePrivate(contextMenu.id, draft?.is_private);
                setContextMenu(null);
              }}>
                <i className={`bi ${drafts.find(d => d.id === contextMenu.id)?.is_private ? 'bi-unlock' : 'bi-lock'} me-2`}></i>
                {drafts.find(d => d.id === contextMenu.id)?.is_private ? 'Make Firm-wide' : 'Make Private'}
              </button>
              <button onClick={() => { openShareModal(contextMenu.id); setContextMenu(null); }}>
                <i className="bi bi-people me-2"></i>Share With...
              </button>
              <hr className="my-1" />
              <button className="text-danger" onClick={() => { deleteDraft(contextMenu.id); setContextMenu(null); }}>
                <i className="bi bi-trash me-2"></i>Delete
              </button>
            </div>
          )}

          {/* Context menu (folder actions) */}
          {contextMenu?.type === 'folder' && (
            <div
              className="soa-context-menu"
              style={{ top: contextMenu.y, left: contextMenu.x }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => {
                const folder = folders.find(f => f.id === contextMenu.id);
                setRenamingFolderId(contextMenu.id);
                setRenamingFolderName(folder?.name || '');
                setContextMenu(null);
              }}>
                <i className="bi bi-pencil me-2"></i>Rename
              </button>
              <button className="text-danger" onClick={() => { deleteFolder(contextMenu.id); setContextMenu(null); }}>
                <i className="bi bi-trash me-2"></i>Delete
              </button>
            </div>
          )}

          {/* Share modal */}
          {shareModal && (
            <div className="soa-modal-overlay" onClick={() => setShareModal(null)}>
              <div className="soa-share-modal" onClick={(e) => e.stopPropagation()}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0" style={{ color: 'var(--primary-color)' }}>
                    <i className="bi bi-people me-2"></i>Share SOA
                  </h5>
                  <button className="btn-close" onClick={() => setShareModal(null)}></button>
                </div>

                <div className="mb-3">
                  <label className="form-label small fw-semibold">Add people from your firm</label>
                  <div className="d-flex gap-2">
                    <select
                      className="form-select form-select-sm"
                      value={shareInput}
                      onChange={(e) => setShareInput(e.target.value)}
                    >
                      <option value="">Select a colleague...</option>
                      {shareModal.domainUsers
                        .filter(u => !shareModal.shares.some(s => s.user_email === u.email))
                        .map(u => (
                          <option key={u.id} value={u.email}>
                            {u.full_name || u.email}
                          </option>
                        ))
                      }
                    </select>
                    <button
                      className="btn btn-sm btn-primary"
                      disabled={!shareInput}
                      onClick={handleAddShare}
                    >
                      <i className="bi bi-plus-lg"></i>
                    </button>
                  </div>
                </div>

                {shareModal.shares.length > 0 ? (
                  <div>
                    <label className="form-label small fw-semibold">Shared with</label>
                    <div className="list-group list-group-flush">
                      {shareModal.shares.map(share => (
                        <div key={share.user_email} className="list-group-item d-flex justify-content-between align-items-center px-0 py-2">
                          <div>
                            <i className="bi bi-person me-2 text-muted"></i>
                            <span className="small">{share.user_email}</span>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleRemoveShare(share.user_email)}
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted small mb-0">
                    Not shared with anyone yet. This SOA will automatically become private when you add someone.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Editor view ───────────────────────────────────────────────────
  return (
    <div className="soa-app">
      <div className="container-fluid py-3">
        {/* Conflict banner */}
        {editConflict && (
          <div className="soa-conflict-banner">
            <div className="d-flex align-items-center gap-2 flex-grow-1">
              <i className="bi bi-exclamation-triangle-fill"></i>
              <span>
                This draft was modified by {editConflict.created_by_email || 'another user'} at{' '}
                {editConflict.updated_at ? new Date(editConflict.updated_at).toLocaleTimeString('en-AU') : 'an unknown time'}.
                Your unsaved changes may conflict.
              </span>
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-light" onClick={reloadLatest}>
                <i className="bi bi-arrow-clockwise me-1"></i>Reload Latest
              </button>
              <button className="btn btn-sm btn-warning" onClick={forceSave}>
                <i className="bi bi-save me-1"></i>Force Save
              </button>
            </div>
          </div>
        )}

        {/* Top bar */}
        <div className="d-flex justify-content-between align-items-center mb-3 px-2">
          <div className="d-flex align-items-center gap-3">
            <button className="btn btn-sm btn-outline-secondary" onClick={() => { setShowDraftsList(true); }}>
              <i className="bi bi-arrow-left me-1"></i>All SOAs
            </button>
            {draftId ? (
              <input
                type="text"
                className="form-control form-control-sm soa-title-input"
                value={draftTitle}
                onChange={(e) => setDraftTitle(e.target.value)}
                placeholder="SOA Title..."
                disabled={isFinalised}
              />
            ) : (
              <span className="text-muted">New Statement of Advice</span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            {isSaving && <span className="text-muted small"><i className="bi bi-arrow-repeat spin me-1"></i>Saving...</span>}
            {lastSaved && !isSaving && (
              <span className="text-muted small">
                <i className="bi bi-check-circle text-success me-1"></i>
                Saved {lastSaved.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {isFinalised && <span className="badge bg-success"><i className="bi bi-lock me-1"></i>Finalised</span>}
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible mx-2 mb-3">
            {error}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        <div className="row g-3 px-2">
          {/* Sidebar */}
          <div className="col-lg-3 col-xl-2">
            <StepSidebar
              currentStep={currentStep}
              setStep={setStep}
              stepDefs={stepDefs}
              stepOrder={stepOrder}
              isFinalised={isFinalised}
              draftId={draftId}
              onCreateDraft={handleStartNew}
              onExportPdf={exportPdf}
              isExporting={isExporting}
              onFinalise={handleFinalise}
              confirmFinalise={confirmFinalise}
              setConfirmFinalise={setConfirmFinalise}
              onTogglePreview={() => setShowPreview(!showPreview)}
              showPreview={showPreview}
              onReset={resetForm}
            />
          </div>

          {/* Main content */}
          <div className={showPreview ? 'col-lg-5 col-xl-6' : 'col-lg-9 col-xl-10'}>
            <div className="soa-step-content">
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                renderStep()
              )}
            </div>
          </div>

          {/* Preview panel */}
          {showPreview && (
            <div className="col-lg-4 col-xl-4">
              <SoaPreview formData={formData} selectedPortfolio={selectedPortfolio} />
            </div>
          )}
        </div>

        {/* History button (floating) */}
        {draftId && (
          <button
            className="btn btn-sm btn-outline-secondary"
            style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 100, borderRadius: '50%', width: '42px', height: '42px' }}
            onClick={handleOpenAuditLog}
            title="View history"
          >
            <i className="bi bi-clock-history"></i>
          </button>
        )}

        {/* Audit log modal */}
        {showAuditLog && (
          <div className="soa-modal-overlay" onClick={() => setShowAuditLog(false)}>
            <div className="soa-share-modal" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0" style={{ color: 'var(--primary-color)' }}>
                  <i className="bi bi-clock-history me-2"></i>History
                </h5>
                <button className="btn-close" onClick={() => setShowAuditLog(false)}></button>
              </div>

              {auditLog.length === 0 ? (
                <p className="text-muted small">No history recorded yet.</p>
              ) : (
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {auditLog.map((entry, i) => (
                    <div key={i} className="d-flex align-items-start gap-2 py-2 border-bottom">
                      <i className={`bi ${
                        entry.action === 'created' ? 'bi-plus-circle text-success' :
                        entry.action === 'finalised' ? 'bi-lock text-primary' :
                        entry.action === 'exported_pdf' ? 'bi-file-pdf text-danger' :
                        entry.action === 'shared' ? 'bi-people text-info' :
                        entry.action === 'unshared' ? 'bi-person-dash text-warning' :
                        entry.action === 'duplicated' ? 'bi-copy text-secondary' :
                        'bi-pencil text-muted'
                      } mt-1`}></i>
                      <div className="flex-grow-1">
                        <div className="small fw-semibold text-capitalize">{entry.action.replace('_', ' ')}</div>
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                          {entry.performed_by_email}
                          {entry.version_at && <span className="ms-2">v{entry.version_at}</span>}
                        </div>
                        {entry.detail && (
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {(() => { try { const d = JSON.parse(entry.detail); return Object.entries(d).map(([k,v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join(' | '); } catch { return entry.detail; } })()}
                          </div>
                        )}
                        {entry.created_at && (
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {new Date(entry.created_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Compliance check modal */}
        {complianceWarnings && (
          <ComplianceCheckModal
            complianceData={complianceWarnings}
            onConfirm={handleComplianceFinalise}
            onCancel={() => setComplianceWarnings(null)}
            onGoToSection={goToSection}
          />
        )}
      </div>
    </div>
  );
};

export default SoaBuilderApp;
